import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { getTrustContents, trustToken, trustLogout } from "@/lib/api";
import PasswordGate from "@/components/trust/PasswordGate";
import TrustHero from "@/components/trust/TrustHero";
import StatsCounters from "@/components/trust/StatsCounters";
import BrandStrategy from "@/components/trust/BrandStrategy";
import ContentGallery from "@/components/trust/ContentGallery";
import ContentModal from "@/components/trust/ContentModal";
import DnaMap from "@/components/trust/DnaMap";
import RegionalDominance from "@/components/trust/RegionalDominance";
import FinalStatement from "@/components/trust/FinalStatement";

export default function TrustCenter() {
  const [data, setData] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [booting, setBooting] = useState(!!trustToken());
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await getTrustContents();
      setData(d);
      setUnlocked(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        trustLogout();
        setUnlocked(false);
      }
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => {
    if (trustToken()) load();
  }, [load]);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [active]);

  const head = (
    <Helmet>
      <html lang="tr" />
      <title>Marka Güven Merkezi · Ertan Kasap</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (!unlocked) {
    return (
      <>
        {head}
        {booting ? (
          <div className="trust-root min-h-[100svh] flex items-center justify-center bg-[#050505]">
            <Loader2 className="w-6 h-6 animate-spin text-[#C48A5E]" />
          </div>
        ) : (
          <PasswordGate onUnlock={load} />
        )}
      </>
    );
  }

  if (!data) {
    return (
      <>
        {head}
        <div className="trust-root min-h-[100svh] flex items-center justify-center bg-[#050505]">
          <Loader2 className="w-6 h-6 animate-spin text-[#C48A5E]" />
        </div>
      </>
    );
  }

  return (
    <>
      {head}
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="trust-root bg-[#050505] selection:bg-[#C48A5E] selection:text-black"
        data-testid="trust-center"
      >
        <TrustHero />
        <StatsCounters counters={data.stats?.counters || []} />
        <BrandStrategy />
        <ContentGallery contents={data.contents || []} onOpen={setActive} />
        <DnaMap dna={data.stats?.dna || []} />
        <RegionalDominance />
        <FinalStatement />
      </motion.main>

      <AnimatePresence>
        {active && (
          <ContentModal
            content={active}
            contents={data.contents || []}
            onNavigate={setActive}
            onClose={() => setActive(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}
